import { useState, useEffect } from "react";
import { useParams, Link, useLoaderData, type LoaderFunctionArgs } from "react-router-dom";
import { lazy, Suspense } from "react";
import { Layout } from "@/components/layout";
import { SEOHead } from "@/components/seo";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { Calendar, User, ArrowRight, Phone, Mail, ChevronRight, MapPin } from "lucide-react";
import { getCommunitiesWithContent, CommunityInfo } from "@/data/communityData";
import { format } from "date-fns";
import { sanitizeHtml } from "@/lib/sanitizeHtml";
import logo from "@/assets/logo.webp";
import { useAgentBranding } from "@/hooks/useAgentBranding";
import AgentBrandingCard from "@/components/agent/AgentBrandingCard";

const NotFound = lazy(() => import("./NotFound"));

interface BlogPostData {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string | null;
  featured_image: string | null;
  author_id: string | null;
  author_name: string;
  status: string;
  category: string | null;
  tags: string[] | null;
  seo_title: string | null;
  seo_description: string | null;
  keywords: string | null;
  created_at: string;
  updated_at: string;
  published_at: string | null;
}

interface AuthorProfile {
  display_name: string | null;
  slug: string | null;
  photo_url: string | null;
  bio: string | null;
  phone: string | null;
  email: string | null;
  headline: string | null;
  agent_role: string | null;
}

// Build-time data loader for SSG. Runs at build for each pre-rendered slug
// AND on client navigations, so post data is available synchronously for
// <SEOHead> to serialize into the static HTML <head>.
export async function loader({ params }: LoaderFunctionArgs) {
  const slug = params.slug;
  if (!slug) return { post: null, author: null };

  const { data: post } = await supabase
    .from("blog_posts")
    .select("*")
    .eq("slug", slug)
    .maybeSingle();

  if (!post) return { post: null, author: null };

  let author: AuthorProfile | null = null;
  if (post.author_id) {
    const { data: authorData } = await supabase
      .from("agents_public")
      .select("display_name, slug, photo_url, bio, phone, email, headline, agent_role")
      .eq("user_id", post.author_id)
      .maybeSingle();
    author = (authorData as AuthorProfile | null) ?? null;
  }

  return { post: post as BlogPostData, author };
}

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const loaderData = useLoaderData() as { post: BlogPostData | null; author: AuthorProfile | null } | undefined;
  const { brandedAgent, isBranded } = useAgentBranding();
  const [post, setPost] = useState<BlogPostData | null>(loaderData?.post ?? null);
  const [author, setAuthor] = useState<AuthorProfile | null>(loaderData?.author ?? null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPostData[]>([]);
  const [loading, setLoading] = useState(!loaderData?.post);
  const [notFound, setNotFound] = useState(loaderData !== undefined && loaderData.post === null);

  useEffect(() => {
    const fetchPost = async () => {
      if (!slug) { setNotFound(true); setLoading(false); return; }

      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("slug", slug)
        .maybeSingle();

      if (error || !data) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      setPost(data as BlogPostData);
      document.title = data.seo_title || `${data.title} | Prime Realty Partners Blog`;

      // Fetch author profile
      if (data.author_id) {
        const { data: authorData } = await supabase
          .from("agents_public")
          .select("display_name, slug, photo_url, bio, phone, email, headline, agent_role")
          .eq("user_id", data.author_id)
          .maybeSingle();
        if (authorData) setAuthor(authorData as AuthorProfile);
      }

      // Fetch related posts
      if (data.category) {
        const { data: related } = await supabase
          .from("blog_posts")
          .select("id, title, slug, excerpt, featured_image, author_name, category, published_at")
          .in("status", ["published", "scheduled"])
          .lte("published_at", new Date().toISOString())
          .eq("category", data.category)
          .neq("id", data.id)
          .order("published_at", { ascending: false })
          .limit(3);
        if (related) setRelatedPosts(related as BlogPostData[]);
      }

      setLoading(false);
    };

    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <Layout>
        <div className="container py-16 max-w-4xl">
          <Skeleton className="h-64 w-full rounded-lg mb-8" />
          <Skeleton className="h-10 w-3/4 mb-4" />
          <Skeleton className="h-6 w-1/2 mb-8" />
          <div className="space-y-4">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-4 w-full" />)}
          </div>
        </div>
      </Layout>
    );
  }

  if (notFound || !post) {
    return <Suspense fallback={null}><NotFound /></Suspense>;
  }

  const canonicalUrl = `https://www.prpsj.com/blog/${post.slug}`;
  const pageTitle = post.seo_title || `${post.title} | Prime Realty Partners Blog`;
  // Strip HTML tags + collapse whitespace, then take first 160 chars as final fallback
  const stripHtml = (html: string) =>
    html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  const pageDescription =
    post.seo_description ||
    post.excerpt ||
    (post.content ? stripHtml(post.content).slice(0, 160) : "");

  const authorName = author?.display_name || post.author_name;
  const isAgentAuthor = !!author;

  const articleSchema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt || "",
    datePublished: post.published_at,
    dateModified: post.updated_at,
    image: post.featured_image || undefined,
    mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
    publisher: {
      "@type": "Organization",
      name: "Prime Realty Partners",
      logo: { "@type": "ImageObject", url: "https://www.prpsj.com/logo.png" },
    },
    author: isAgentAuthor
      ? {
          "@type": "Person",
          name: authorName,
          url: author?.slug ? `https://www.prpsj.com/agent/${author.slug}` : undefined,
          image: author?.photo_url || undefined,
          jobTitle: author?.headline || undefined,
          worksFor: { "@type": "RealEstateBrokerage", name: "Prime Realty Partners" },
        }
      : { "@type": "Organization", name: "Prime Realty Partners" },
  };

  return (
    <Layout>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        canonicalUrl={canonicalUrl}
        keywords={post.keywords || undefined}
        ogType="article"
        ogImage={post.featured_image || "https://www.prpsj.com/og-image.png"}
        schema={[articleSchema]}
        noindex={isBranded}
      />

      {/* Hero */}
      {post.featured_image ? (
        <section className="relative h-64 md:h-96 overflow-hidden">
          <img src={post.featured_image} alt={post.title} width={1200} height={400} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
        </section>
      ) : (
        <section className="bg-gradient-to-br from-primary/15 via-primary/5 to-background py-12" />
      )}

      <article className="container max-w-4xl py-8 md:py-12">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
          <Link to="/" className="hover:text-primary transition-colors">Home</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/blog" className="hover:text-primary transition-colors">Blog</Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-foreground font-medium line-clamp-1">{post.title}</span>
        </div>

        {post.category && (
          <Badge variant="secondary" className="mb-4">{post.category}</Badge>
        )}

        <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
          {post.title}
        </h1>

        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-8">
          <span className="flex items-center gap-1.5">
            <User className="h-4 w-4" />
            {authorName}
          </span>
          {post.published_at && (
            <span className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4" />
              {format(new Date(post.published_at), "MMMM d, yyyy")}
            </span>
          )}
        </div>

        {/* Content */}
        <div
          className="prose prose-lg max-w-none prose-headings:font-heading prose-headings:font-bold prose-a:text-primary hover:prose-a:text-primary/80 prose-img:rounded-lg prose-p:mb-4 prose-p:leading-relaxed"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(post.content) }}
        />

        {/* Featured Communities */}
        {(() => {
          if (!post.tags || post.tags.length === 0) return null;
          const allCommunities = getCommunitiesWithContent();
          const matched = allCommunities.filter(c =>
            post.tags!.some(tag => tag.toLowerCase() === c.name.toLowerCase())
          );
          if (matched.length === 0) return null;
          return (
            <div className="mt-12 pt-8 border-t border-border">
              <h2 className="font-heading text-2xl font-bold mb-6">Explore These Communities</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {matched.map((community) => (
                  <Link key={community.slug} to={`/${community.slug}`} className="group">
                    <Card className="hover:shadow-lg transition-shadow h-full">
                      <CardContent className="pt-6">
                        <div className="flex items-start gap-3">
                          <MapPin className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <h3 className="font-heading font-semibold group-hover:text-primary transition-colors">
                              {community.name}
                            </h3>
                            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                              {community.tagline}
                            </p>
                            <p className="text-xs text-muted-foreground mt-2">
                              {community.county} County · {community.zips.join(", ")}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Author Card */}
        <div className="mt-12 pt-8 border-t border-border">
          {isAgentAuthor ? (
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col sm:flex-row gap-6">
                    {author?.photo_url && (
                    <img
                      src={author.photo_url}
                      alt={`${authorName} — Real Estate Agent at Prime Realty Partners`}
                      width={96}
                      height={96}
                      className="h-24 w-24 rounded-full object-cover shrink-0"
                    />
                  )}
                  <div className="flex-1">
                    <h3 className="font-heading text-xl font-bold">{authorName}</h3>
                    {author?.headline && (
                      <p className="text-sm text-muted-foreground mb-2">{author.headline}</p>
                    )}
                    {author?.bio && (
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-3">{author.bio}</p>
                    )}
                    <div className="flex flex-wrap gap-3">
                      {author?.slug && (
                        <Button size="sm" variant="outline" asChild>
                          <Link to={`/agent/${author.slug}`}>View Profile</Link>
                        </Button>
                      )}
                      {author?.phone && (
                        <Button size="sm" variant="outline" asChild>
                          <a href={`tel:${author.phone}`} className="gap-1.5">
                            <Phone className="h-3.5 w-3.5" />
                            Contact {authorName.split(" ")[0]}
                          </a>
                        </Button>
                      )}
                      {author?.email && (
                        <Button size="sm" variant="outline" asChild>
                          <a href={`mailto:${author.email}`} className="gap-1.5">
                            <Mail className="h-3.5 w-3.5" />
                            Email
                          </a>
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : isBranded && brandedAgent ? (
            <AgentBrandingCard agent={brandedAgent} />
          ) : (
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col sm:flex-row gap-6 items-center">
                  <img src={logo} alt="Prime Realty Partners logo" width={160} height={64} className="h-16 w-auto object-contain" />
                  <div className="flex-1 text-center sm:text-left">
                    <h3 className="font-heading text-xl font-bold">Prime Realty Partners</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Experience You Can Trust. Results You Deserve. Serving South Jersey home buyers and sellers across Camden, Burlington, and Gloucester counties.
                    </p>
                    <Button size="sm" asChild>
                      <Link to="/contact">Contact Us</Link>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <div className="mt-12">
            <h2 className="font-heading text-2xl font-bold mb-6">Related Posts</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {relatedPosts.map((rp) => (
                <Link key={rp.id} to={`/blog/${rp.slug}`} className="group">
                  <Card className="hover:shadow-lg transition-shadow h-full">
                    {rp.featured_image && (
                      <img src={rp.featured_image} alt={rp.title} width={400} height={128} className="h-32 w-full object-cover rounded-t-lg" loading="lazy" />
                    )}
                    <CardContent className="pt-4">
                      <h3 className="font-heading font-semibold text-sm group-hover:text-primary transition-colors line-clamp-2">
                        {rp.title}
                      </h3>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Bottom CTA */}
        <div className="mt-12 pt-8 border-t border-border text-center">
          <h2 className="font-heading text-2xl font-bold mb-3">
            Looking for Homes in South Jersey?
          </h2>
          <p className="text-muted-foreground mb-6">
            Browse our community guides to find the perfect neighborhood.
          </p>
          <Button size="lg" asChild>
            <Link to="/communities" className="gap-2">
              Explore Communities
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </article>
    </Layout>
  );
};

export default BlogPost;
